/**
 * The colours of a psychological safety result: the score and the agreement each get their own.
 * A copy of the backend model (trustpulse-api/src/domain/survey/team-result.ts and
 * team-agreement.ts), tested against the same cases, so pages and emails always agree.
 */
export type ResultColour = 'GREEN' | 'YELLOW' | 'RED';
export type TeamAgreement = 'HIGH' | 'MODERATE' | 'LOW';
export type ScoreStatus = 'success' | 'warning' | 'danger' | 'zero';
export type Tone = Exclude<ScoreStatus, 'zero'>;

/** Read on the 1–5 scale: below 3 the team leans towards disagreeing with the statements, from 4 it agrees */
const SCORE_LIMITS = { yellowFrom: 3, greenFrom: 4 } as const;

/** Below 0.5 the team agrees; up to and including 1.0 it mostly agrees; above it disagrees */
const AGREEMENT_LIMITS = { highBelow: 0.5, moderateUpTo: 1.0 } as const;

export const scoreColourOf = (score: number): ResultColour => {
  if (score < SCORE_LIMITS.yellowFrom) return 'RED';
  if (score < SCORE_LIMITS.greenFrom) return 'YELLOW';
  return 'GREEN';
};

export const teamAgreementOf = (standardDeviation: number): TeamAgreement => {
  if (standardDeviation < AGREEMENT_LIMITS.highBelow) return 'HIGH';
  if (standardDeviation <= AGREEMENT_LIMITS.moderateUpTo) return 'MODERATE';
  return 'LOW';
};

const isKnownSpread = (standardDeviation: number | null | undefined): standardDeviation is number =>
  typeof standardDeviation === 'number' && Number.isFinite(standardDeviation) && standardDeviation >= 0;

/** Null when there is no spread to read, so the page shows no agreement at all */
export const agreementOf = (standardDeviation: number | null | undefined): TeamAgreement | null =>
  isKnownSpread(standardDeviation) ? teamAgreementOf(standardDeviation) : null;

export const agreementColour: Record<TeamAgreement, ResultColour> = {
  HIGH: 'GREEN',
  MODERATE: 'YELLOW',
  LOW: 'RED',
};

export const toneOf: Record<ResultColour, Tone> = {
  GREEN: 'success',
  YELLOW: 'warning',
  RED: 'danger',
};

const isKnownScore = (score: number | null | undefined): score is number =>
  typeof score === 'number' && Number.isFinite(score) && score > 0;

/** From the score alone; disagreement is shown on its own and does not move the colour */
export const scoreStatusOf = (score: number | null | undefined): ScoreStatus =>
  isKnownScore(score) ? toneOf[scoreColourOf(score)] : 'zero';
